import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ERROR_CODE } from 'src/app/shared/models/error.models';
import { environment } from 'src/environments/environment';
import { MAX_STORAGE_HTTP_RETRIES, STORAGE_RETRY_COUNT } from './storage-retry.context';

interface StorageRetryErrorBody {
	retry?: boolean;
	storage_retry?: boolean;
	error?: {
		error_code?: number | string;
	};
}

/**
 * Reintenta automáticamente peticiones al journey cuando el back responde KO 2099 + storage_retry.
 *
 * Capa B del diseño NIF-646: el back ya esperó hasta 25s (poll GCS) en el intento fallido;
 * aquí reenviamos la misma HttpRequest sin pausa ni reset de captura en el componente OCR.
 *
 * Solo aplica a URLs de environment.identity2. No interfiere con retry OCR (2001, 2032, 2072…)
 * porque exige el flag storage_retry además de retry: true.
 */
@Injectable()
export class StorageRetryInterceptor implements HttpInterceptor {
	public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		if (!this.shouldIntercept(req.url)) {
			return next.handle(req);
		}

		return this.handleWithStorageRetry(req, next);
	}

	/**
	 * Cada intento vuelve a pasar por catchError; si solo se hace next.handle(retryReq)
	 * sin pipe, solo habría un reintento (2 HTTP) en lugar de MAX_STORAGE_HTTP_RETRIES.
	 */
	private handleWithStorageRetry(
		req: HttpRequest<unknown>,
		next: HttpHandler,
	): Observable<HttpEvent<unknown>> {
		return next.handle(req).pipe(
			catchError((error: unknown) => {
				if (!(error instanceof HttpErrorResponse) || !this.isStorageRetryError(error)) {
					return throwError(() => error);
				}

				const attempt = req.context.get(STORAGE_RETRY_COUNT);
				if (attempt >= MAX_STORAGE_HTTP_RETRIES) {
					// Agotados los reintentos: el componente recibe el error (salvaguarda OCR/POL).
					return throwError(() => error);
				}

				const retryReq = req.clone({
					context: req.context.set(STORAGE_RETRY_COUNT, attempt + 1),
				});

				// Reintento inmediato: la espera ya ocurrió en el servidor durante el poll GCS.
				return this.handleWithStorageRetry(retryReq, next);
			}),
		);
	}

	private shouldIntercept(url: string): boolean {
		const apiBase = environment.identity2;
		return Boolean(apiBase && url.includes(apiBase));
	}

	private isStorageRetryError(error: HttpErrorResponse): boolean {
		if (error.status !== 400) {
			return false;
		}

		const body = error.error as StorageRetryErrorBody | null | undefined;
		// storage_retry distingue fallo de almacenamiento de KO OCR recuperable (misma retry: true).
		if (!body?.storage_retry || body.retry !== true) {
			return false;
		}

		const code = body.error?.error_code;
		return (
			code === 2099 ||
			code === '2099' ||
			code === ERROR_CODE.STORAGE_NOT_AVAILABLE ||
			Number(code) === 2099
		);
	}
}
